import { useMemo, useReducer, useCallback, useEffect } from "react";

const STORAGE_KEY = "wizardCreation:dataOffering:v1";

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const initialState = {
  stepIndex: 0,
  data: {
    step1: {
      name: "",
      descriptionText: "",
      title: [],
      description: [],
    },
    step2: { configById: [], selectedPolicies: [], rowSelection: {} },
    step3: { selectedIris: [], selectedDatasets: [], rowSelection: {} },
    step4: {},
  },
};

function getInitialState(stepsCount) {
  const persisted = safeParse(sessionStorage.getItem(STORAGE_KEY), null);

  if (!persisted) return initialState;

  return {
    stepIndex: clamp(persisted.stepIndex ?? 0, 0, Math.max(0, stepsCount - 1)),
    data: {
      ...initialState.data,
      ...(persisted.data ?? {}),
    },
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "REHYDRATE": {
      return action.payload;
    }
    case "SET_STEP_DATA":
      return {
        ...state,
        data: {
          ...state.data,
          [action.stepKey]: {
            ...state.data[action.stepKey],
            ...action.payload,
          },
        },
      };
    case "NEXT":
      return { ...state, stepIndex: Math.min(state.stepIndex + 1, action.max) };
    case "BACK":
      return { ...state, stepIndex: Math.max(state.stepIndex - 1, 0) };
    case "GOTO":
      return { ...state, stepIndex: action.index };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export function useWizardController(stepsCount) {
  const [state, dispatch] = useReducer(reducer, stepsCount, getInitialState);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const max = Math.max(0, stepsCount - 1);
    if (state.stepIndex > max) {
      dispatch({ type: "GOTO", index: max });
    }
  }, [stepsCount, state.stepIndex]);

  const setStepData = useCallback((stepKey, payload) => {
    dispatch({ type: "SET_STEP_DATA", stepKey, payload });
  }, []);

  const next = useCallback(() => {
    dispatch({ type: "NEXT", max: stepsCount - 1 });
  }, [stepsCount]);

  const back = useCallback(() => dispatch({ type: "BACK" }), []);
  const goto = useCallback((index) => dispatch({ type: "GOTO", index }), []);

  const reset = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    dispatch({ type: "RESET" });
  }, []);

  return useMemo(
    () => ({ state, setStepData, next, back, goto, reset }),
    [state, setStepData, next, back, goto, reset],
  );
}
